import type { SupabaseClient } from '@supabase/supabase-js';
import { z } from 'zod';

export function isCollateralDate(value: string): boolean {
  if (value === '') {
    return true;
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || value.startsWith('0000')) {
    return false;
  }
  const date = new Date(`${value}T12:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

export const primisCollateralSnapshotSchema = z.object({
  id: z.string(),
  primis_collateral_requested_date: z.string().refine(isCollateralDate).nullable(),
  primis_collateral_requested_revision: z.union([z.string().regex(/^\d+$/), z.number().int().nonnegative().safe()]).transform(String),
  primis_collateral_requested_set_by: z.string().nullable(),
  primis_collateral_requested_set_by_name: z.string().nullable(),
  primis_collateral_requested_set_at: z.string().nullable()
});
export type PrimisCollateralSnapshot = z.infer<typeof primisCollateralSnapshotSchema>;
const resultSchema = z.object({ saved: z.boolean(), snapshot: primisCollateralSnapshotSchema });
const columns = Object.keys(primisCollateralSnapshotSchema.shape).join(',');

interface CollateralState {
  snapshot: PrimisCollateralSnapshot | null;
  saving: boolean;
  checking: boolean;
  uncertain: boolean;
  error: string | null;
}
interface Entry {
  state: CollateralState;
  listeners: Set<() => void>;
  read: Promise<boolean> | null;
  uncertainValue?: string;
}

/** Portal-only dates share confirmed state; drafts belong to each editor.
 * Existing loan reads/realtime feed this store. It adds no polling or queue.
 */
export class PrimisCollateralStore {
  private entries = new Map<string, Entry>();
  constructor(private client: SupabaseClient, private organizationId: string) {}

  private entry(id: string): Entry {
    let entry = this.entries.get(id);
    if (!entry) {
      entry = { state: { snapshot: null, saving: false, checking: false, uncertain: false, error: null }, listeners: new Set(), read: null };
      this.entries.set(id, entry);
    }
    return entry;
  }
  get = (id: string) => this.entry(id).state;
  subscribe(id: string, listener: () => void) {
    const entry = this.entry(id);
    entry.listeners.add(listener);
    return () => {
      entry.listeners.delete(listener);
    };
  }
  private update(id: string, patch: Partial<CollateralState>) {
    const entry = this.entry(id);
    entry.state = { ...entry.state, ...patch };
    entry.listeners.forEach(listener => listener());
  }
  accept(snapshot: PrimisCollateralSnapshot) {
    const current = this.get(snapshot.id).snapshot;
    if (!current || BigInt(snapshot.primis_collateral_requested_revision) > BigInt(current.primis_collateral_requested_revision)) {
      this.update(snapshot.id, { snapshot });
    }
    const entry = this.entry(snapshot.id);
    if (entry.uncertainValue !== undefined
      && (entry.state.snapshot?.primis_collateral_requested_date ?? '') === entry.uncertainValue) {
      entry.uncertainValue = undefined;
      this.update(snapshot.id, { uncertain: false, error: null });
    }
  }
  check(id: string): Promise<boolean> {
    const entry = this.entry(id);
    if (entry.read) {
      return entry.read;
    }
    this.update(id, { checking: true });
    entry.read = (async () => {
      try {
        const { data, error } = await this.client.from('encompass_loans').select(columns)
          .eq('id', id).eq('organization_id', this.organizationId).abortSignal(AbortSignal.timeout(10_000)).maybeSingle();
        if (error) {
          throw error;
        }
        const parsed = primisCollateralSnapshotSchema.parse(data);
        if (parsed.id !== id) {
          throw new Error('Loan mismatch');
        }
        this.accept(parsed);
        if (this.get(id).error === 'Could not check the collateral request date. Try again.') {
          this.update(id, { error: this.get(id).uncertain ? "Couldn't confirm the collateral request date. Check again or retry." : null });
        }
        return true;
      } catch {
        this.update(id, { error: 'Could not check the collateral request date. Try again.' });
        return false;
      } finally {
        entry.read = null;
        this.update(id, { checking: false });
      }
    })();
    return entry.read;
  }
  async save(id: string, value: string, expectedRevision: string, retry = false): Promise<boolean> {
    if (this.get(id).saving || !isCollateralDate(value)) {
      return false;
    }
    this.update(id, { saving: true, error: null });
    try {
      // Recheck an uncertain response before issuing any replacement. The CAS
      // also protects a timed-out first request that completes after this read.
      if ((retry || this.get(id).uncertain) && !await this.check(id)) {
        return false;
      }
      const current = this.get(id).snapshot;
      if (!current) {
        this.update(id, { error: 'Check the collateral request date before saving.' });
        return false;
      }
      const { data, error } = await this.client.rpc('set_loan_primis_collateral_requested_date', {
        p_loan_id: id,
        p_requested_date: value || null,
        p_expected_revision: retry ? current.primis_collateral_requested_revision : expectedRevision
      }).abortSignal(AbortSignal.timeout(15_000));
      if (error) {
        throw error;
      }
      const result = resultSchema.parse(data);
      if (result.snapshot.id !== id) {
        throw new Error('Loan mismatch');
      }
      this.accept(result.snapshot);
      this.entry(id).uncertainValue = undefined;
      this.update(id, {
        uncertain: false,
        error: result.saved ? null : 'This date changed elsewhere. Review the saved date before saving your edit.'
      });
      return result.saved;
    } catch {
      this.entry(id).uncertainValue = value;
      this.update(id, { uncertain: true });
      const checked = await this.check(id);
      const current = this.get(id).snapshot;
      if (checked && current && (current.primis_collateral_requested_date ?? '') === value) {
        this.update(id, { uncertain: false, error: null });
        return true;
      }
      this.update(id, { error: "Couldn't confirm the collateral request date. Check again or retry." });
      return false;
    } finally {
      this.update(id, { saving: false });
    }
  }
}

const stores = new WeakMap<SupabaseClient, Map<string, PrimisCollateralStore>>();
export function getPrimisCollateralStore(client: SupabaseClient, userId: string, organizationId: string) {
  let scopes = stores.get(client);
  if (!scopes) {
    scopes = new Map();
    stores.set(client, scopes);
  }
  const key = `${userId}:${organizationId}`;
  let store = scopes.get(key);
  if (!store) {
    store = new PrimisCollateralStore(client, organizationId);
    scopes.set(key, store);
  }
  return store;
}
